import { OktonSelect } from '@okton/platform-ui';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import type { ChangeEvent, ReactNode } from 'react';
import {
  Box,
  Button,
  Stack,
  Flex,
  Text,
  Heading,
  Container,
  VStack,
  useColorModeValue,
  IconButton,
  useToast,
  useDisclosure,
  SimpleGrid,
  FormControl,
  FormLabel,
  Switch,
  HStack,
} from '@chakra-ui/react';
import { FiArrowLeft, FiZap } from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import type { AxiosError } from 'axios';

import backend from '../../services/backend';
import { useAuth } from '../../hooks/auth';
import Page from '../../layout/IntegraAppPage';

import { SecaoInformacoesBasicas } from './components/SecaoInformacoesBasicas';
import { SecaoPrecoEstoque } from './components/SecaoPrecoEstoque';
import { SecaoAtributos } from './components/SecaoAtributos';
import type {
  AtributoProduto,
  AtributoCustomizado,
} from './components/SecaoAtributos';
import { SecaoUploadImagens } from './components/SecaoUploadImagens';
import ModalVisualizarImagens from './components/ModalVisualizarImagens';

type SecaoCardProps = {
  title: string;
  subtitle?: string;
  children: ReactNode;
  bg: string;
  borderColor: string;
};

type ImagemProduto = {
  id: string;
  url: string;
  arquivoOriginal: File;
};

type CategoriaMercadoLivre = {
  ml_id: string;
  nome_categoria?: string;
};

type PredictorData = {
  categoria?: CategoriaMercadoLivre;
  atributos_sugeridos?: Record<string, string>;
};

type ApiError = AxiosError<{ message?: string }>;

type InputLikeEvent = ChangeEvent<
  HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
>;

const SecaoCard = ({
  title,
  subtitle,
  children,
  bg,
  borderColor,
}: SecaoCardProps) => {
  const corTitulo = useColorModeValue('gray.800', 'whiteAlpha.900');
  const corSubtitulo = useColorModeValue('gray.600', 'gray.400');

  return (
    <Box
      bg={bg}
      p={6}
      borderRadius="lg"
      border="1px"
      borderColor={borderColor}
      shadow="sm"
    >
      <VStack align="start" spacing={1} mb={6}>
        <Heading size="sm" fontWeight="bold" color={corTitulo}>
          {title}
        </Heading>

        {subtitle && (
          <Text fontSize="xs" color={corSubtitulo}>
            {subtitle}
          </Text>
        )}
      </VStack>

      {children}
    </Box>
  );
};

const NovoProduto: React.FC = () => {
  const { user } = useAuth();
  const history = useHistory();
  const aviso = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const referenciaInputArquivo = useRef<HTMLInputElement>(null);

  const cores = {
    fundoPagina: useColorModeValue('gray.50', 'gray.900'),
    fundoCard: useColorModeValue('white', 'gray.800'),
    corBorda: useColorModeValue('gray.200', 'gray.700'),
    fundoInput: useColorModeValue('white', 'gray.700'),
    textoLabel: useColorModeValue('gray.700', 'gray.300'),
  };

  const [carregando, setCarregando] = useState(false);
  const [indiceImagemModal, setIndiceImagemModal] = useState(0);
  const [atributos, setAtributos] = useState<AtributoProduto[]>([]);
  const [imagens, setImagens] = useState<ImagemProduto[]>([]);
  const [atributosCustomizados, setAtributosCustomizados] = useState<
    AtributoCustomizado[]
  >([{ id: 'attr-1', trait: '', value: '' }]);

  const [categoriaMl, setCategoriaMl] = useState<CategoriaMercadoLivre | null>(
    null,
  );
  const [tipoAnuncioMl, setTipoAnuncioMl] = useState('gold_special');
  const [freteGratisMl, setFreteGratisMl] = useState(false);
  const [retiradaLocalMl, setRetiradaLocalMl] = useState(true);

  const [dadosFormulario, setDadosFormulario] = useState({
    codigoInterno: '',
    sku: '',
    titulo: '',
    marca: '',
    categoria_id: '',
    categoria_ml: '',
    categoria_nome: '',
    codigoBarras: '',
    ncm: '',
    status: 'Ativo',
    descricaoCurta: '',
    descricaoCompleta: '',
    preco: '0',
    precoPromocional: '',
    estoqueTotal: '0',
    estoqueReservado: '0',
    unidadeMedida: 'Unidade (UN)',
    peso: '',
    altura: '',
    largura: '',
    profundidade: '',
    permitirAutomatico: true,
  });

  const normalizarAtributo = (
    attr: AtributoProduto,
    sugeridos: Record<string, string> = {},
  ): AtributoProduto => {
    const id = attr.id || attr.atributo_externo_id;
    const nome = attr.name || attr.nome || id;

    return {
      ...attr,
      id,
      name: nome,
      value:
        attr.value ||
        attr.value_name ||
        attr.valor_nome ||
        sugeridos?.[id] ||
        '',
    };
  };

  const lidarComLogicaIntegracao = useCallback(
    async (titulo: string) => {
      if (!dadosFormulario.permitirAutomatico || titulo.length < 8) return;

      setCarregando(true);

      try {
        const resposta = await backend.post('/mercadolivre/analyze', {
          titulo,
          empresa: user?.empresa_id,
          filial: user?.filial_id,
        });

        const dados = (resposta.data.data || resposta.data) as PredictorData;

        if (!dados.categoria) {
          return;
        }

        setCategoriaMl(dados.categoria);

        setDadosFormulario(prev => ({
          ...prev,
          categoria_ml: dados.categoria.ml_id,
          categoria_nome: dados.categoria.nome_categoria,
          marca: prev.marca || dados.atributos_sugeridos?.BRAND || '',
        }));

        const respostaAttrs = await backend.get(
          `/mercadolivre/categories/${dados.categoria.ml_id}/attributes`,
        );

        const attrs = (respostaAttrs.data.data ||
          respostaAttrs.data ||
          []) as AtributoProduto[];

        const attrsTela = attrs
          .filter(attr => {
            const tags = attr.tags || {};
            const id = String(attr.id || '').toUpperCase();
            const nome = String(attr.nome || attr.name || '').toLowerCase();

            const obrigatorio =
              attr.obrigatorio ||
              attr.required ||
              attr.catalog_required ||
              attr.conditional_required ||
              attr.fixed ||
              tags.required ||
              tags.catalog_required ||
              tags.conditional_required ||
              tags.fixed;

            const oculto =
              tags.hidden ||
              tags.used_hidden ||
              tags.new_hidden ||
              attr.hidden ||
              attr.used_hidden ||
              attr.new_hidden;

            const atributoTecnicoPrincipal = attr.relevance === 1;

            const pareceCampoOperacional =
              nome.includes('embalagem') ||
              nome.includes('pacote') ||
              nome.includes('vendedor') ||
              nome.includes('imposto') ||
              nome.includes('certificado') ||
              nome.includes('certificação') ||
              nome.includes('subtel') ||
              nome.includes('hazmat') ||
              nome.includes('portabilidade') ||
              nome.includes('sku') ||
              id.includes('PACKAGE') ||
              id.includes('SELLER') ||
              id.includes('TAX') ||
              id.includes('CERT') ||
              id.includes('SKU');

            if (oculto && !obrigatorio) {
              return false;
            }

            return (
              obrigatorio ||
              (atributoTecnicoPrincipal && !pareceCampoOperacional)
            );
          })
          .map(attr => normalizarAtributo(attr, dados.atributos_sugeridos));

        setAtributos(attrsTela);
      } catch {
        // Mantém o preenchimento manual disponível quando o Predictor falhar.
      } finally {
        setCarregando(false);
      }
    },
    [dadosFormulario.permitirAutomatico, user?.empresa_id, user?.filial_id],
  );

  useEffect(() => {
    const temporizador = setTimeout(() => {
      if (dadosFormulario.titulo) {
        lidarComLogicaIntegracao(dadosFormulario.titulo);
      }
    }, 1500);

    return () => clearTimeout(temporizador);
  }, [dadosFormulario.titulo, lidarComLogicaIntegracao]);

  const lidarComMudancaInput = (e: InputLikeEvent) => {
    const { name, value } = e.target;
    setDadosFormulario(prev => ({ ...prev, [name]: value }));
  };

  const lidarComUploadImagem = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (!files) return;

    const listaArquivos = Array.from(files);

    if (imagens.length + listaArquivos.length > 10) {
      aviso({
        title: 'Limite excedido',
        description: 'Você pode enviar no máximo 10 imagens.',
        status: 'warning',
      });
      return;
    }

    const novasImagens = listaArquivos.map(arquivo => ({
      id: `${Date.now()}-${Math.random()}`,
      url: URL.createObjectURL(arquivo),
      arquivoOriginal: arquivo,
    }));

    setImagens(prev => [...prev, ...novasImagens]);

    if (referenciaInputArquivo.current) {
      referenciaInputArquivo.current.value = '';
    }
  };

  const montarAtributosMercadoLivre = () => {
    return atributos
      .map(attr => ({
        atributo_externo_id: attr.id,
        nome: attr.name || attr.nome || attr.id,
        valor_externo_id: attr.valor_externo_id || attr.value_id || null,
        valor_nome: attr.value || attr.value_name || attr.valor_nome || '',
      }))
      .filter(
        attr =>
          attr.atributo_externo_id &&
          attr.valor_nome !== null &&
          attr.valor_nome !== '',
      );
  };

  const lidarComSalvarProduto = async () => {
    if (!dadosFormulario.titulo || !dadosFormulario.sku) {
      aviso({
        title: 'Preencha os campos obrigatórios (*)',
        status: 'warning',
      });
      return;
    }

    setCarregando(true);

    try {
      const formData = new FormData();

      Object.entries(dadosFormulario).forEach(([chave, valor]) =>
        formData.append(chave, String(valor)),
      );

      formData.append('empresa_id', user?.empresa_id || '');
      formData.append('filial_id', user?.filial_id || '');

      imagens.forEach((img, index) =>
        formData.append(`imagens[${index}]`, img.arquivoOriginal),
      );

      const atributosML = atributos
        .map(attr => ({
          id: attr.id,
          value_name: attr.value_name || attr.value || '',
        }))
        .filter(attr => attr.id && attr.value_name);

      formData.append('atributos_ml', JSON.stringify(atributosML));

      const produtoResponse = await backend.post(
        '/produtos/completo',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      );

      const produtoCriado =
        produtoResponse.data.data ||
        produtoResponse.data.produto ||
        produtoResponse.data;

      if (
        produtoCriado?.id &&
        (categoriaMl?.ml_id || dadosFormulario.categoria_ml)
      ) {
        await backend.post(`/produtos/${produtoCriado.id}/anuncios`, {
          plataforma: 'mercadolivre',
          categoria_externa_id:
            categoriaMl?.ml_id || dadosFormulario.categoria_ml,
          tipo_anuncio: tipoAnuncioMl,
          preco_venda: Number(dadosFormulario.preco || 0),
          estoque: Number(dadosFormulario.estoqueTotal || 0),
          frete: {
            mode: 'me2',
            free_shipping: freteGratisMl,
            local_pick_up: retiradaLocalMl,
          },
          atributos: montarAtributosMercadoLivre(),
        });
      }

      aviso({
        title: 'Produto criado com sucesso!',
        description: 'O rascunho do Mercado Livre também foi salvo.',
        status: 'success',
      });

      history.push('/produtos/completo');
    } catch (error) {
      const err = error as ApiError;
      aviso({
        title: 'Erro ao salvar produto',
        description: err.response?.data?.message || 'Tente novamente.',
        status: 'error',
      });
    } finally {
      setCarregando(false);
    }
  };

  return (
    <Page>
      <Box
        bg={cores.fundoPagina}
        minH="100vh"
        py={6}
        color={useColorModeValue('gray.800', 'white')}
      >
        <Container maxW="1600px">
          <Flex align="center" mb={6} gap={4}>
            <IconButton
              aria-label="Voltar"
              icon={<FiArrowLeft />}
              variant="ghost"
              onClick={() => history.goBack()}
              borderRadius="full"
            />

            <VStack align="start" spacing={0}>
              <Heading size="md">Novo Produto</Heading>
              <Text fontSize="xs" color="gray.500">
                Preencha os detalhes do item abaixo
              </Text>
            </VStack>
          </Flex>

          <Stack spacing={6}>
            <SecaoCard
              title="Informações Básicas"
              bg={cores.fundoCard}
              borderColor={cores.corBorda}
            >
              <SecaoInformacoesBasicas
                dadosFormulario={dadosFormulario}
                lidarComMudancaInput={lidarComMudancaInput}
                carregando={carregando}
                cores={cores}
              />
            </SecaoCard>

            <SecaoCard
              title="Mercado Livre"
              subtitle="Dados usados para criar o rascunho do anúncio"
              bg={cores.fundoCard}
              borderColor={cores.corBorda}
            >
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <OktonSelect
                  name="tipo-anuncio-mercado-livre"
                  label="Tipo de anúncio"
                  size="sm"
                  value={tipoAnuncioMl}
                  onChange={e => setTipoAnuncioMl(e.target.value)}
                >
                  <option value="gold_special">Clássico</option>
                  <option value="gold_pro">Premium</option>
                  <option value="free">Grátis</option>
                </OktonSelect>

                <FormControl>
                  <FormLabel
                    fontSize="xs"
                    fontWeight="bold"
                    color={cores.textoLabel}
                  >
                    Frete grátis
                  </FormLabel>
                  <HStack h="32px">
                    <Switch
                      isChecked={freteGratisMl}
                      onChange={e => setFreteGratisMl(e.target.checked)}
                      colorScheme="blue"
                    />
                    <Text fontSize="sm">{freteGratisMl ? 'Sim' : 'Não'}</Text>
                  </HStack>
                </FormControl>

                <FormControl>
                  <FormLabel
                    fontSize="xs"
                    fontWeight="bold"
                    color={cores.textoLabel}
                  >
                    Retirada local
                  </FormLabel>
                  <HStack h="32px">
                    <Switch
                      isChecked={retiradaLocalMl}
                      onChange={e => setRetiradaLocalMl(e.target.checked)}
                      colorScheme="blue"
                    />
                    <Text fontSize="sm">{retiradaLocalMl ? 'Sim' : 'Não'}</Text>
                  </HStack>
                </FormControl>
              </SimpleGrid>
            </SecaoCard>

            <SecaoAtributos
              atributos={atributos}
              setAtributos={setAtributos}
              atributosCustomizados={atributosCustomizados}
              setAtributosCustomizados={setAtributosCustomizados}
              adicionarAtributoCustomizado={() =>
                setAtributosCustomizados([
                  ...atributosCustomizados,
                  { id: Date.now().toString(), trait: '', value: '' },
                ])
              }
              cores={cores}
            />

            <SecaoCard
              title="Preços e Estoque"
              bg={cores.fundoCard}
              borderColor={cores.corBorda}
            >
              <SecaoPrecoEstoque
                dadosFormulario={dadosFormulario}
                lidarComMudancaInput={lidarComMudancaInput}
                cores={cores}
              />
            </SecaoCard>

            <SecaoCard
              title={`Imagens (${imagens.length}/10)`}
              subtitle="Imagens do Anúncio"
              bg={cores.fundoCard}
              borderColor={cores.corBorda}
            >
              <input
                type="file"
                hidden
                ref={referenciaInputArquivo}
                accept="image/*"
                multiple
                onChange={lidarComUploadImagem}
              />

              <SecaoUploadImagens
                imagens={imagens}
                abrirModalImagem={idx => {
                  setIndiceImagemModal(idx);
                  onOpen();
                }}
                removerImagem={id =>
                  setImagens(prev => prev.filter(img => img.id !== id))
                }
                carregarFotos={() => referenciaInputArquivo.current?.click()}
                carregando={carregando}
                cores={cores}
              />
            </SecaoCard>

            <Flex justify="flex-end" gap={3} py={6} pb={12}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => history.goBack()}
                borderRadius="md"
              >
                Cancelar
              </Button>

              <Button
                colorScheme="blue"
                size="sm"
                leftIcon={<FiZap />}
                isLoading={carregando}
                borderRadius="md"
                onClick={lidarComSalvarProduto}
              >
                Salvar Produto
              </Button>
            </Flex>
          </Stack>

          <ModalVisualizarImagens
            isOpen={isOpen}
            onClose={onClose}
            imagens={imagens}
            indiceAtual={indiceImagemModal}
            setIndiceAtual={setIndiceImagemModal}
          />
        </Container>
      </Box>
    </Page>
  );
};

export default NovoProduto;
